import { create } from 'zustand';
import { findingsApi } from '../lib/findings-api';
import type { Finding } from '../lib/findings-api';

// Demo findings for demo mode
const DEMO_FINDINGS: Finding[] = [
  {
    id: 'demo-f1',
    type: 'insight',
    priority: 1,
    title: 'Contract review pending',
    description: 'Sarah sent the Q2 contract 3 days ago. Follow up before the Friday deadline.',
    sourceType: 'email',
    sourceEntity: 'email-1',
    actionLabel: 'Review contract',
    actionUrl: '/email/thread-1',
    createdAt: new Date().toISOString(),
    dismissed: false,
  },
  {
    id: 'demo-f2',
    type: 'reminder',
    priority: 2,
    title: 'Q1 report is overdue',
    description: 'The Q1 report was due 2 days ago. Complete and submit to the leadership team.',
    sourceType: 'todo',
    sourceEntity: 'todo-3',
    actionLabel: 'View task',
    actionUrl: '/todo/todo-3',
    createdAt: new Date().toISOString(),
    dismissed: false,
  },
  {
    id: 'demo-f3',
    type: 'insight',
    priority: 2,
    title: 'High-priority tasks due today',
    description: 'You have 2 high-priority tasks due today. Focus on these first.',
    sourceType: 'todo',
    sourceEntity: 'todo-1',
    actionLabel: 'View tasks',
    actionUrl: '/todo/todo-1',
    createdAt: new Date().toISOString(),
    dismissed: false,
  },
  {
    id: 'demo-f4',
    type: 'insight',
    priority: 3,
    title: 'Meeting notes need review',
    description: 'Sprint retrospective notes from April 7 are missing action item owners.',
    sourceType: 'note',
    sourceEntity: 'note-2',
    actionLabel: 'Review notes',
    actionUrl: '/notes/note-2',
    createdAt: new Date().toISOString(),
    dismissed: false,
  },
];

interface FindingsState {
  findings: Finding[];
  isLoading: boolean;
  error: string | null;
  activeFilter: 'all' | 'email' | 'todo' | 'note';
  loadFindings(): Promise<void>;
  refreshFindings(): Promise<void>;
  dismissFinding(id: string): Promise<void>;
  setActiveFilter(filter: 'all' | 'email' | 'todo' | 'note'): void;
  clearError(): void;
  loadDemo(): void;
}

export const useFindingsStore = create<FindingsState>((set, get) => ({
  findings: [],
  isLoading: false,
  error: null,
  activeFilter: 'all',

  loadFindings: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await findingsApi.load();
      set({ findings: res.findings, isLoading: false });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load findings',
      });
    }
  },

  refreshFindings: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await findingsApi.refresh();
      set({ findings: res.findings, isLoading: false });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to refresh findings',
      });
    }
  },

  dismissFinding: async (id: string) => {
    const previous = get().findings;
    // Optimistic dismiss
    set((s) => ({ findings: s.findings.filter((f) => f.id !== id) }));
    try {
      await findingsApi.dismiss(id);
    } catch (err) {
      // Revert on failure
      set({ findings: previous, error: err instanceof Error ? err.message : 'Failed to dismiss finding' });
    }
  },

  setActiveFilter: (filter) => set({ activeFilter: filter }),

  clearError: () => set({ error: null }),

  loadDemo: () => {
    set({ findings: DEMO_FINDINGS, isLoading: false, error: null });
  },
}));

// Demo Mode
if (typeof window !== 'undefined') {
  window.addEventListener('signa:demo-mode', () => {
    const state = useFindingsStore.getState();
    if (state.findings.length === 0) {
      useFindingsStore.getState().loadDemo();
    }
  });
}
